import React, { useRef } from 'react'

import autoplayButton from '@assets/side-button-1.svg?inline'
import autoplayButtonEQ from '@assets/jackpot-eq/side-button-1.svg?inline'
import ultraPlayAutoplayIcon from '@assets/ultra-play/ultra-play-icon-autoplay-modal.svg?inline'

import {
    SpinButtonSectionStore,
    useConfigurationStore,
    useJackpotStore,
    useMenuStore,
    useNavigationStore,
    useSoundStore,
    useSpinButtonSectionStore,
    useUltraPlayStore,
} from '@stores'
import { SideButton } from '../'
import { BetButton } from '../../'
import styles from '../SpinButtonSection.module.css'
import useOpacityToggle from '@hooks/useOpacityToggle'
import { useHasActiveBonus } from '@hooks'
import { SOUNDS } from '@constants/sounds'
import {
    AutoplayButtonClickedMessage,
    BetChooserStartsClosingMessage,
    BetChooserStartsOpeningMessage,
    ChangeOpacityComponents,
    CollectClickedMessage,
    GameUIMessageType,
    MenuSectionsIds,
    OpenBetChooserClickedMessage,
    ViewportType,
} from 'game-ui-types'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'
import { extractScale } from '@utils/misc'
import { cn } from '@amusnet/react-ui'

interface AutoplayButtonProps {
    isActive: boolean
    shouldUpdateOpacityWhenInactive?: boolean
    shouldActAsCollect: boolean
}

const AutoplayButton: React.FC<AutoplayButtonProps> = ({
    isActive,
    shouldUpdateOpacityWhenInactive,
    shouldActAsCollect,
}) => {
    const isEQJackpotType = useJackpotStore((state) => state.isEQJackpotType)

    const autoplayOptions = useConfigurationStore((state) => state.autoplayOptions)
    const hasUltraPlay = useConfigurationStore((state) => state.hasUltraPlay)
    const isUltraPlaySupported = useConfigurationStore((state) => state.isUltraPlaySupported)
    const openUltraPlay = useUltraPlayStore((state) => state.open)
    const playEngineSound = useSoundStore((state) => state.playEngineSound)
    // Bet is locked during a bonus, so the Ultra Play entry is disabled then.
    const hasActiveBonus = useHasActiveBonus()
    const isAutoplayDisabled = useConfigurationStore((state) => state.isAutoplayDisabled)
    const shouldHideDisabledAutoplayButton = useConfigurationStore((state) => state.shouldHideDisabledAutoplayButton)
    const viewport = useConfigurationStore((state) => state.viewport)
    const shouldOpenAutoplaySettings = useConfigurationStore((state) => state.shouldOpenAutoplaySettings)

    const isLeftHandMode = useSpinButtonSectionStore((state: SpinButtonSectionStore) => state.isLeftHandMode)

    const isAutoplaySectionOpened = useSpinButtonSectionStore((state) => state.isAutoplaySectionOpened)
    const toggleAutoplaySection = useSpinButtonSectionStore((state) => state.toggleAutoplaySection)
    const updateSelectedAutoplayIndex = useSpinButtonSectionStore((state) => state.updateSelectedAutoplayIndex)
    const updateAutoplayCount = useSpinButtonSectionStore((state) => state.updateAutoplayCount)
    const updateAutoplayMode = useSpinButtonSectionStore((state) => state.updateAutoplayMode)
    const close = useSpinButtonSectionStore((state) => state.close)

    const setIsMenuOpen = useNavigationStore((state) => state.setIsMenuOpen)
    const setTargetMenuSectionId = useMenuStore((state) => state.setTargetMenuSectionId)

    const buttonConfigStyles = useConfigurationStore((state) => state.getComponentConfig('AutoplayButton').styles)

    const sectionConfigStyles = useConfigurationStore((state) => state.getComponentConfig('AutoplaySection').styles)
    const sectionConfigPositions = useConfigurationStore(
        (state) => state.getComponentConfig('AutoplaySection').position
    )

    const sectionLeftHandConfigPositions = useConfigurationStore(
        (state) => state.getComponentConfig('AutoplaySectionLeftHand').position
    )

    const sectionConfigPosition = isLeftHandMode ? sectionLeftHandConfigPositions : sectionConfigPositions

    const autoplayButtonComponent = useRef<HTMLDivElement | null>(null)
    const autoplayModalRef = useRef<HTMLDivElement | null>(null)

    useOpacityToggle(ChangeOpacityComponents.AUTOPLAY_BUTTON, autoplayButtonComponent)

    useGSAP(() => {
        if (!autoplayModalRef.current) return

        const el = autoplayModalRef.current

        if (!isAutoplaySectionOpened) return

        const isPortrait = viewport === ViewportType.MOBILE_PORTRAIT
        const modalScale = extractScale(sectionConfigStyles ?? '') || 1

        let axis = 'x'
        let scaleAxis = 'scaleX'
        let offset = 20
        let origin = 'right center'

        if (isPortrait) {
            axis = 'y'
            scaleAxis = 'scaleY'
            offset = 20
            origin = 'bottom center'
        } else {
            scaleAxis = 'scaleX'
            axis = 'x'

            if (isLeftHandMode) {
                offset = -20
                origin = 'left center'
            } else {
                offset = 20
                origin = 'right center'
            }
        }

        gsap.fromTo(
            el,
            {
                opacity: 0.5,
                [scaleAxis]: 0,
                [axis]: offset,
                transformOrigin: origin,
            },
            {
                opacity: 1,
                [scaleAxis]: modalScale,
                [axis]: 0,
                duration: 0.35,
                ease: 'power2.out',
            }
        )
    }, [isAutoplaySectionOpened, viewport, isLeftHandMode])

    const handleAutoplayOptionClick = (amount: number, index: number) => {
        const autoplayButtonClickedMessage: AutoplayButtonClickedMessage = {
            type: GameUIMessageType.AUTOPLAY_BUTTON_CLICKED,
            payload: index,
        }

        window.postMessage(autoplayButtonClickedMessage, '*')

        updateAutoplayMode(true)
        updateAutoplayCount(amount !== -1 ? autoplayOptions[index] - 1 : autoplayOptions[index])
        updateSelectedAutoplayIndex(index)
        close()
    }

    // Last option in the modal — opens the Ultra Play feature instead of starting engine autoplay.
    const handleUltraPlayClick = () => {
        playEngineSound(SOUNDS.SHOW_DENOMINATIONS)
        openUltraPlay()
        close()
    }

    const renderAutoplayButtons = () => {
        return autoplayOptions.map((amount, index) => (
            <div key={index}>
                <BetButton
                    key={index}
                    label=""
                    amount={amount === -1 ? '∞' : amount.toString()}
                    shouldShowCurrency={false}
                    isActive={false}
                    isForbidden={false}
                    onClick={() => handleAutoplayOptionClick(amount, index)}
                    size={{ width: '108px', height: '76px' }}
                    id={`autoplay-option-${amount === -1 ? '∞' : amount.toString()}`}
                    className="relative flex flex-col items-center justify-center text-white focus:outline-none px-8"
                />
            </div>
        ))
    }

    const autoplaySectionStyles =
        viewport === ViewportType.MOBILE_PORTRAIT ? 'my-[20px] mx-[24px] flex-col' : 'mx-[20px] my-[24px] flex-row'

    const onSideButtonClicked = () => {
        if (shouldActAsCollect) {
            const collectClickedMessage: CollectClickedMessage = { type: GameUIMessageType.COLLECT_CLICKED }

            window.postMessage(collectClickedMessage, '*')

            return
        }

        if (shouldOpenAutoplaySettings) {
            setTargetMenuSectionId(MenuSectionsIds.AUTOPLAY_SETTINGS)
            setIsMenuOpen(true)

            return
        }

        const openChooserClickedMessage: OpenBetChooserClickedMessage = {
            type: GameUIMessageType.OPEN_BET_CHOOSER_CLICKED,
        }

        window.postMessage(openChooserClickedMessage, '*')

        if (isAutoplaySectionOpened) {
            const chooserStartsClosingMessage: BetChooserStartsClosingMessage = {
                type: GameUIMessageType.BET_CHOOSER_STARTS_CLOSING,
            }

            window.postMessage(chooserStartsClosingMessage, '*')
        } else {
            const chooserStartsOpeningMessage: BetChooserStartsOpeningMessage = {
                type: GameUIMessageType.BET_CHOOSER_STARTS_OPENING,
            }

            window.postMessage(chooserStartsOpeningMessage, '*')
        }

        toggleAutoplaySection()
    }

    if (isAutoplayDisabled && shouldHideDisabledAutoplayButton) {
        // Keep the button invisible but keep the width and height for portrait mode dispositioning
        return (
            <div style={{ opacity: 0 }}>
                <SideButton
                    id="autoplay-button"
                    isActive={false}
                    onClick={() => ({})}
                    texture={!isEQJackpotType ? autoplayButton : autoplayButtonEQ}
                    className={cn('z-[50]', buttonConfigStyles)}
                    shouldUpdateOpacityWhenInactive={false}
                />
            </div>
        )
    }

    return (
        <div ref={autoplayButtonComponent} className="flex">
            <SideButton
                id="autoplay-button"
                isActive={isAutoplayDisabled ? false : isActive}
                onClick={onSideButtonClicked}
                texture={!isEQJackpotType ? autoplayButton : autoplayButtonEQ}
                className={cn(isAutoplaySectionOpened ? 'z-[101]' : 'z-[50]', buttonConfigStyles)}
                shouldUpdateOpacityWhenInactive={isAutoplayDisabled ? true : shouldUpdateOpacityWhenInactive}
            />

            {!shouldOpenAutoplaySettings && (
                <div
                    ref={autoplayModalRef}
                    className={cn(
                        viewport === ViewportType.MOBILE_PORTRAIT ? 'origin-bottom' : 'origin-right',
                        styles.modalGradientBorder,
                        `${sectionConfigStyles} ${isAutoplaySectionOpened ? 'z-[101]' : 'hidden'}`
                    )}
                    style={{ ...sectionConfigPosition, margin: 0 }}
                >
                    <div className={cn('flex gap-[16px]', autoplaySectionStyles)}>
                        {!isAutoplayDisabled && renderAutoplayButtons()}
                        {!isAutoplayDisabled && hasUltraPlay && isUltraPlaySupported && (
                            <div key="ultra-play">
                                <button
                                    id="autoplay-option-ultra-play"
                                    onClick={hasActiveBonus ? undefined : handleUltraPlayClick}
                                    style={{ width: '108px', height: '76px' }}
                                    className={cn(
                                        'relative flex items-center justify-center focus:outline-none',
                                        hasActiveBonus && 'opacity-50 pointer-events-none'
                                    )}
                                >
                                    <img src={ultraPlayAutoplayIcon} alt="Ultra Play" className="w-full h-full" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default AutoplayButton
